import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { api, ApiError, downloadFile, mutate } from '../shared/api';
import { formatDate, formatMoney, formatNumber } from '../shared/formatting';
import { OrderTransfersPanel } from './orderTransfersPanel';

type Locale = 'hr' | 'en';
type Nacin = 'vozac' | 'hp' | 'gls';

type Item = {
    sifra: string | null;
    naziv: string;
    kolicina: number;
    dostupno: number;
    nedostaje: number;
    poznat_artikl: boolean;
    iz: Record<string, number>;
};

type Row = {
    order_number: string;
    poslovnica: string;
    mjesto_isporuke: string | null;
    pohrana: boolean;
    datum_unosa: string | null;
    iznos_eur: number;
    status_bis: string | null;
    status_bis_label: string | null;
    datum_isporuke: string | null;
    buduci_datum: boolean;
    napomena: string | null;
    razlog: string | null;
    items: Item[];
};

type PackedRow = {
    paket_id: number | null;
    order_number: string;
    poslovnica: string;
    mjesto_isporuke: string | null;
    pohrana: boolean;
    datum_unosa: string | null;
    iznos_eur: number;
    datum_pakiranja: string | null;
    napomena: string | null;
    items: { naziv: string; kolicina: number }[];
};

type TransferredRow = {
    order_number: string;
    poslovnica: string;
    mjesto_isporuke: string | null;
    pohrana: boolean;
    datum_unosa: string | null;
    iznos_eur: number;
    status_bis_label: string | null;
    napomena: string | null;
    nova_narudzba: string;
    nova_poslovnica: string | null;
    faros_status: string | null;
    vec_pakirano: boolean;
    datum_pakiranja: string | null;
    paket_id: number | null;
};

type Paket = {
    id: number;
    poslovnica: string | null;
    pohrana: boolean;
    status: string;
    datum_pakiranja: string | null;
    pakirao: string | null;
    broj_narudzbi: number;
    broj_komada: number;
    iznos_eur: number;
    narudzbe: string[];
    prebacene: { order_number: string; nova_narudzba: string }[];
};

type Resp = {
    as_of: { narudzbe: string | null; zaliha_batch: string | null; zaliha_datum: string | null; skladista: string[] };
    ready: Row[];
    incomplete: Row[];
    packed: PackedRow[];
    transferred: TransferredRow[];
    packages: Paket[];
    excluded: Record<string, number>;
};

type MarkResp = {
    ok: boolean;
    marked: { order_number: string; value: string }[];
    skipped: { order_number: string; razlog: string }[];
};

const NACIN_LABEL: Record<Nacin, { hr: string; en: string }> = {
    vozac: { hr: 'Vozač', en: 'Driver' },
    hp: { hr: 'HP', en: 'HP' },
    gls: { hr: 'GLS', en: 'GLS' },
};

/**
 * Modul 40.43, Preskladistenje, priprema (mail UID 938, Ruzica Sarajlic).
 * Popis otvorenih narudzbi za koje postoji zaliha na 212 i 210, sortiran
 * POHRANA / ostalo pa poslovnica pa datum. Oznake "na putu" (vozac, HP, GLS)
 * idu grupno; broj preskladistenja se ne unosi. Narudzbe bez pune pokrivenosti
 * zalihom nisu ponudjene za oznacavanje.
 */
export function RestockPrepModule({ locale, token, onBack }: { locale: Locale; token: string | null; onBack?: () => void }) {
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);

    const [data, setData] = useState<Resp | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [selected, setSelected] = useState<Set<string>>(new Set());
    const [nacin, setNacin] = useState<Nacin | ''>('');
    const [branch, setBranch] = useState('');
    const [dateFrom, setDateFrom] = useState('');
    const [dateTo, setDateTo] = useState('');
    const [expanded, setExpanded] = useState<Set<string>>(new Set());
    const [busy, setBusy] = useState(false);
    const [result, setResult] = useState<MarkResp | null>(null);
    const [showIncomplete, setShowIncomplete] = useState(false);

    const load = useCallback(() => {
        setLoading(true);
        setError(null);
        api<Resp>('/restock-prep', token)
            .then((r) => {
                setData(r);
                setSelected(new Set());
            })
            .catch((e) => setError(e instanceof ApiError ? e.message : t('Učitavanje nije uspjelo.', 'Loading failed.')))
            .finally(() => setLoading(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token, locale]);

    useEffect(() => {
        load();
    }, [load]);

    const branches = useMemo(() => {
        const set = new Set<string>();
        (data?.ready ?? []).forEach((r) => set.add(r.poslovnica));
        (data?.incomplete ?? []).forEach((r) => set.add(r.poslovnica));
        return Array.from(set).sort((a, b) => a.localeCompare(b, 'hr'));
    }, [data]);

    const passes = useCallback(
        (r: { poslovnica: string; datum_unosa: string | null }) => {
            if (branch && r.poslovnica !== branch) return false;
            const d = (r.datum_unosa ?? '').slice(0, 10);
            if (dateFrom && d < dateFrom) return false;
            if (dateTo && d > dateTo) return false;
            return true;
        },
        [branch, dateFrom, dateTo],
    );

    const ready = useMemo(() => (data?.ready ?? []).filter(passes), [data, passes]);
    const incomplete = useMemo(() => (data?.incomplete ?? []).filter(passes), [data, passes]);
    const packed = useMemo(() => (data?.packed ?? []).filter(passes), [data, passes]);
    const transferred = useMemo(() => (data?.transferred ?? []).filter(passes), [data, passes]);
    const transferredPacked = transferred.filter((r) => r.vec_pakirano);
    const readyNormal = ready.filter((r) => !r.pohrana);
    const readyPohrana = ready.filter((r) => r.pohrana);

    const toggle = (on: string) =>
        setSelected((prev) => {
            const next = new Set(prev);
            if (next.has(on)) next.delete(on);
            else next.add(on);
            return next;
        });

    const toggleGroup = (rows: { order_number: string }[]) =>
        setSelected((prev) => {
            const next = new Set(prev);
            const all = rows.length > 0 && rows.every((r) => next.has(r.order_number));
            rows.forEach((r) => (all ? next.delete(r.order_number) : next.add(r.order_number)));
            return next;
        });

    const toggleExpanded = (on: string) =>
        setExpanded((prev) => {
            const next = new Set(prev);
            if (next.has(on)) next.delete(on);
            else next.add(on);
            return next;
        });

    // Odabir se cuva i kad filter sakrije redak, ali se salje samo ono sto je vidljivo.
    const selectedReady = ready.filter((r) => selected.has(r.order_number));
    // Narudzba u paketu salje se samo s paketom, pa se pojedinacno biraju samo pakirane bez paketa.
    const packedLoose = packed.filter((r) => r.paket_id === null);
    const packages = useMemo(() => {
        const byId = new Map<number, PackedRow[]>();
        packed.forEach((r) => {
            if (r.paket_id !== null) byId.set(r.paket_id, [...(byId.get(r.paket_id) ?? []), r]);
        });
        const visible = new Set(transferred.map((r) => r.paket_id).filter((v): v is number => v !== null));
        return (data?.packages ?? [])
            .filter((p) => byId.has(p.id) || visible.has(p.id))
            .map((p) => ({ ...p, rows: byId.get(p.id) ?? [] }));
    }, [data, packed, transferred]);
    const selectedPacked = packedLoose.filter((r) => selected.has(r.order_number));
    const sendable = [...selectedReady, ...selectedPacked];
    const needsNacin = sendable.some((r) => !r.pohrana);
    const canPack = selectedReady.length > 0 && !busy;
    const canSend = sendable.length > 0 && !busy && (!needsNacin || nacin !== '');

    const packageAction = async (p: Paket, akcija: 'send' | 'cancel' | 'remove', orderNumber?: string) => {
        if (busy) return;
        if (akcija === 'send' && !p.pohrana && nacin === '') {
            setError(t('Za slanje paketa odaberi način slanja.', 'Choose a carrier to send the package.'));
            return;
        }
        const msg =
            akcija === 'send'
                ? t(
                      `Označiti paket #${p.id} (${p.poslovnica ?? ''}, ${p.broj_narudzbi} narudžbi) kao poslano${p.pohrana ? ', pohranjeno' : ` uz način slanja ${nacin ? NACIN_LABEL[nacin][locale] : ''}`}?`,
                      `Mark package #${p.id} (${p.poslovnica ?? ''}, ${p.broj_narudzbi} orders) as sent?`,
                  )
                : akcija === 'cancel'
                  ? t(`Otkazati paket #${p.id}? Narudžbe se vraćaju na popis spremnih.`, `Cancel package #${p.id}? Orders go back to ready.`)
                  : t(`Izvaditi ${orderNumber ?? ''} iz paketa #${p.id}? Oznaka pakirano se skida.`, `Take ${orderNumber ?? ''} out of package #${p.id}?`);
        if (!window.confirm(msg)) return;
        setBusy(true);
        setError(null);
        try {
            const body = akcija === 'send' ? (p.pohrana ? {} : { nacin }) : akcija === 'remove' ? { order_number: orderNumber } : {};
            const res = await mutate<Partial<MarkResp>>(`/restock-prep/packages/${p.id}/${akcija}`, token, 'POST', body);
            if (akcija === 'send') setResult({ ok: true, marked: res.marked ?? [], skipped: res.skipped ?? [] });
            load();
        } catch (e) {
            setError(e instanceof ApiError ? e.message : t('Radnja nad paketom nije uspjela.', 'Package action failed.'));
        } finally {
            setBusy(false);
        }
    };

    const mark = async (akcija: 'pakirano' | 'na_putu', paket = false) => {
        const rows = akcija === 'pakirano' ? selectedReady : sendable;
        if (rows.length === 0 || busy || (akcija === 'na_putu' && !canSend)) return;
        const nPohrana = rows.filter((r) => r.pohrana).length;
        const nOther = rows.length - nPohrana;
        const label = nacin ? NACIN_LABEL[nacin][locale] : '';
        const nBranches = new Set(rows.map((r) => `${r.poslovnica}|${r.pohrana ? 1 : 0}`)).size;
        const msg =
            akcija === 'pakirano'
                ? paket
                    ? t(
                          `Pakirati ${rows.length} narudžbi u ${nBranches === 1 ? 'jedan paket' : `${nBranches} paketa, po jedan za svaku poslovnicu`}?`,
                          `Pack ${rows.length} orders into ${nBranches} package(s), one per branch?`,
                      )
                    : t(`Označiti ${rows.length} narudžbi kao pakirano?`, `Mark ${rows.length} orders as packed?`)
                : t(
                      `Označiti ${rows.length} narudžbi kao poslano? ${nOther > 0 ? `${nOther} uz način slanja ${label}. ` : ''}${nPohrana > 0 ? `${nPohrana} kao pohranjeno.` : ''}`,
                      `Mark ${rows.length} orders as sent? ${nOther > 0 ? `${nOther} via ${label}. ` : ''}${nPohrana > 0 ? `${nPohrana} as stored.` : ''}`,
                  );
        if (!window.confirm(msg)) return;
        setBusy(true);
        setError(null);
        try {
            const res = await mutate<MarkResp>('/restock-prep/mark', token, 'POST', {
                orders: rows.map((r) => r.order_number),
                akcija,
                ...(akcija === 'pakirano' && paket ? { paket: true } : {}),
                ...(akcija === 'na_putu' && nacin ? { nacin } : {}),
            });
            setResult(res);
            load();
        } catch (e) {
            setError(e instanceof ApiError ? e.message : t('Označavanje nije uspjelo.', 'Marking failed.'));
        } finally {
            setBusy(false);
        }
    };

    const exportXlsx = async () => {
        try {
            const q = new URLSearchParams();
            if (branch) q.set('branch', branch);
            if (dateFrom) q.set('date_from', dateFrom);
            if (dateTo) q.set('date_to', dateTo);
            const qs = q.toString();
            await downloadFile(`/restock-prep/export.xlsx${qs ? `?${qs}` : ''}`, token,`preskladistenje-priprema-${new Date().toISOString().slice(0, 10)}.xlsx`);
        } catch {
            setError(t('Izvoz nije uspio.', 'Export failed.'));
        }
    };

    const th: React.CSSProperties = { textAlign: 'left', padding: '0.35rem 0.5rem', borderBottom: '1px solid #999', whiteSpace: 'nowrap' };
    const td: React.CSSProperties = { padding: '0.3rem 0.5rem', borderBottom: '1px solid #ddd', verticalAlign: 'top' };
    const num: React.CSSProperties = { ...td, textAlign: 'right', whiteSpace: 'nowrap' };

    const renderTable = (rows: Row[], opts: { markable: boolean; title: string }) => (
        <section style={{ marginTop: '1.2rem' }}>
            <h3 style={{ margin: '0 0 0.4rem' }}>
                {opts.title} ({rows.length})
            </h3>
            {rows.length === 0 ? (
                <p className="muted">{t('Nema narudžbi.', 'No orders.')}</p>
            ) : (
                <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.88rem' }}>
                        <thead>
                            <tr>
                                {opts.markable ? (
                                    <th style={th}>
                                        <input
                                            type="checkbox"
                                            aria-label={t('Označi sve', 'Select all')}
                                            checked={rows.every((r) => selected.has(r.order_number))}
                                            onChange={() => toggleGroup(rows)}
                                        />
                                    </th>
                                ) : null}
                                <th style={th}>{t('Poslovnica', 'Branch')}</th>
                                <th style={th}>{t('Mjesto isporuke', 'Delivery place')}</th>
                                <th style={th}>{t('Narudžba', 'Order')}</th>
                                <th style={th}>{t('Datum unosa', 'Entered')}</th>
                                <th style={{ ...th, textAlign: 'right' }}>{t('Iznos s PDV-om', 'Amount incl. VAT')}</th>
                                <th style={th}>{t('Status u BIS-u', 'BIS status')}</th>
                                <th style={th}>{t('Budući datum isporuke', 'Future delivery date')}</th>
                                <th style={th}>{t('Napomena', 'Note')}</th>
                                <th style={th}>{opts.markable ? t('Artikli', 'Items') : t('Razlog', 'Reason')}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((r) => {
                                const open = expanded.has(r.order_number);
                                return (
                                    <React.Fragment key={r.order_number}>
                                        <tr>
                                            {opts.markable ? (
                                                <td style={td}>
                                                    <input type="checkbox" checked={selected.has(r.order_number)} onChange={() => toggle(r.order_number)} />
                                                </td>
                                            ) : null}
                                            <td style={td}>{r.poslovnica}</td>
                                            <td style={td}>{r.mjesto_isporuke ?? ''}</td>
                                            <td style={td}>{r.order_number}</td>
                                            <td style={td}>{r.datum_unosa ? formatDate(r.datum_unosa, locale) : ''}</td>
                                            <td style={num}>{formatMoney(r.iznos_eur, locale)}</td>
                                            <td style={td}>{r.status_bis_label ?? ''}</td>
                                            <td style={td}>{r.buduci_datum && r.datum_isporuke ? formatDate(r.datum_isporuke, locale) : ''}</td>
                                            <td style={td}>{r.napomena ?? ''}</td>
                                            <td style={td}>
                                                {opts.markable ? (
                                                    <button type="button" className="secondary compact" onClick={() => toggleExpanded(r.order_number)}>
                                                        {open ? t('Sakrij', 'Hide') : t(`${r.items.length} st.`, `${r.items.length} lines`)}
                                                    </button>
                                                ) : (
                                                    <>
                                                        {r.razlog}{' '}
                                                        {r.items.length > 0 ? (
                                                            <button type="button" className="secondary compact" onClick={() => toggleExpanded(r.order_number)}>
                                                                {open ? t('Sakrij', 'Hide') : t('Stavke', 'Lines')}
                                                            </button>
                                                        ) : null}
                                                    </>
                                                )}
                                            </td>
                                        </tr>
                                        {open ? (
                                            <tr>
                                                <td style={{ ...td, background: '#f5f5f5' }} colSpan={opts.markable ? 10 : 9}>
                                                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.84rem' }}>
                                                        <thead>
                                                            <tr>
                                                                <th style={th}>{t('Šifra', 'Code')}</th>
                                                                <th style={th}>{t('Artikal', 'Item')}</th>
                                                                <th style={{ ...th, textAlign: 'right' }}>{t('Količina', 'Qty')}</th>
                                                                <th style={{ ...th, textAlign: 'right' }}>{t('Dostupno', 'Available')}</th>
                                                                <th style={th}>{t('Skladište', 'Warehouse')}</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {r.items.map((it, i) => (
                                                                <tr key={`${it.sifra ?? it.naziv}-${i}`}>
                                                                    <td style={td}>{it.sifra ?? ''}</td>
                                                                    <td style={td}>{it.naziv}</td>
                                                                    <td style={num}>{formatNumber(it.kolicina, locale)}</td>
                                                                    <td style={num}>{formatNumber(it.dostupno, locale)}</td>
                                                                    <td style={td}>
                                                                        {Object.entries(it.iz ?? {})
                                                                            .filter(([, q]) => q > 0)
                                                                            .map(([w, q]) => `${w}: ${formatNumber(q, locale)}`)
                                                                            .join(', ')}
                                                                    </td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </table>
                                                </td>
                                            </tr>
                                        ) : null}
                                    </React.Fragment>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}
        </section>
    );

    return (
        <div className="module-shell">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: '1rem', flexWrap: 'wrap' }}>
                <div>
                    {onBack ? (
                        <button type="button" className="secondary compact" onClick={onBack}>
                            ← {t('Skladište', 'Warehouse')}
                        </button>
                    ) : null}
                    <p className="eyebrow">40.43</p>
                    <h2 style={{ margin: '0.2rem 0' }}>{t('Preskladištenje, priprema', 'Restocking preparation')}</h2>
                    <p className="muted" style={{ margin: 0 }}>
                        {t(
                            'Otvorene narudžbe koje se u cijelosti mogu pokriti zalihom na CENTRALNO REDOVNE NARUDŽBE (212) i ADO-KOMISIJA (210). Zaliha je količina kakva stoji u skladištu, bez umanjenja za smješteno.',
                            'Open orders that can be covered in full from stock on warehouses 212 and 210. Stock is the quantity as it stands, not reduced for reserved goods.',
                        )}
                    </p>
                    {data ? (
                        <p className="muted" style={{ margin: '0.2rem 0 0', fontSize: '0.82rem' }}>
                            {t('Narudžbe na dan', 'Orders as of')} {data.as_of.narudzbe ? formatDate(data.as_of.narudzbe, locale) : '-'} · {t('zaliha na dan', 'stock as of')}{' '}
                            {data.as_of.zaliha_datum ? formatDate(data.as_of.zaliha_datum, locale) : '-'}
                        </p>
                    ) : null}
                </div>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <button type="button" className="secondary compact" onClick={load} disabled={loading}>
                        {t('Osvježi', 'Refresh')}
                    </button>
                    <button type="button" className="secondary compact" onClick={exportXlsx} disabled={!data}>
                        {t('Izvoz u Excel', 'Export to Excel')}
                    </button>
                </div>
            </div>

            {error ? (
                <p role="alert" style={{ color: '#a00', marginTop: '0.8rem' }}>
                    {error}
                </p>
            ) : null}

            {result ? (
                <div role="status" style={{ border: '1px solid #999', padding: '0.5rem 0.8rem', marginTop: '0.8rem' }}>
                    <p style={{ margin: 0 }}>
                        {t(`Označeno ${result.marked.length}, preskočeno ${result.skipped.length}.`, `Marked ${result.marked.length}, skipped ${result.skipped.length}.`)}
                    </p>
                    {result.skipped.length > 0 ? (
                        <ul style={{ margin: '0.3rem 0 0', paddingLeft: '1.2rem' }}>
                            {result.skipped.map((s) => (
                                <li key={s.order_number}>
                                    {s.order_number}: {s.razlog}
                                </li>
                            ))}
                        </ul>
                    ) : null}
                    <button type="button" className="secondary compact" onClick={() => setResult(null)}>
                        {t('Zatvori', 'Close')}
                    </button>
                </div>
            ) : null}

            <div style={{ display: 'flex', gap: '0.8rem', flexWrap: 'wrap', alignItems: 'flex-end', marginTop: '1rem' }}>
                <label>
                    <span className="muted" style={{ display: 'block', fontSize: '0.78rem' }}>{t('Poslovnica', 'Branch')}</span>
                    <select value={branch} onChange={(e) => setBranch(e.target.value)}>
                        <option value="">{t('Sve', 'All')}</option>
                        {branches.map((b) => (
                            <option key={b} value={b}>
                                {b}
                            </option>
                        ))}
                    </select>
                </label>
                <label>
                    <span className="muted" style={{ display: 'block', fontSize: '0.78rem' }}>{t('Datum unosa od', 'Entered from')}</span>
                    <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
                </label>
                <label>
                    <span className="muted" style={{ display: 'block', fontSize: '0.78rem' }}>{t('do', 'to')}</span>
                    <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
                </label>
                <span style={{ flex: 1 }} />
                <label>
                    <span className="muted" style={{ display: 'block', fontSize: '0.78rem' }}>{t('Način slanja', 'Carrier')}</span>
                    <select value={nacin} onChange={(e) => setNacin(e.target.value as Nacin | '')}>
                        <option value="">{t('Odaberi', 'Choose')}</option>
                        {(Object.keys(NACIN_LABEL) as Nacin[]).map((k) => (
                            <option key={k} value={k}>
                                {NACIN_LABEL[k][locale]}
                            </option>
                        ))}
                    </select>
                </label>
                <button type="button" className="compact" onClick={() => mark('pakirano')} disabled={!canPack}>
                    {t(`Označi pakirano (${selectedReady.length})`, `Mark packed (${selectedReady.length})`)}
                </button>
                <button
                    type="button"
                    className="compact"
                    onClick={() => mark('pakirano', true)}
                    disabled={!canPack}
                    title={t('Odabrane narudžbe idu u jedan paket po poslovnici.', 'Selected orders go into one package per branch.')}
                >
                    {t(`Pakiraj u paket (${selectedReady.length})`, `Pack as package (${selectedReady.length})`)}
                </button>
                <button type="button" className="compact" onClick={() => mark('na_putu')} disabled={!canSend}>
                    {t(`Označi poslano (${sendable.length})`, `Mark sent (${sendable.length})`)}
                </button>
            </div>
            {sendable.length > 0 && needsNacin && nacin === '' ? (
                <p className="muted" style={{ fontSize: '0.82rem', margin: '0.3rem 0 0' }}>
                    {t('Za narudžbe koje nisu POHRANA treba odabrati način slanja.', 'Choose a carrier for orders that are not POHRANA.')}
                </p>
            ) : null}

            {loading && !data ? <p className="muted">{t('Učitavam…', 'Loading…')}</p> : null}

            {data ? (
                <>
                    {renderTable(readyNormal, { markable: true, title: t('Spremno za slanje', 'Ready to send') })}
                    {renderTable(readyPohrana, { markable: true, title: t('POHRANA (status pohranjeno)', 'POHRANA (stored)') })}

                    <section style={{ marginTop: '1.2rem' }}>
                        <h3 style={{ margin: '0 0 0.4rem' }}>
                            {t('Pakirano, čeka slanje', 'Packed, waiting to be sent')} ({packed.length})
                        </h3>
                        {packages.map((p) => (
                            <div key={p.id} style={{ border: '1px solid #999', padding: '0.5rem 0.7rem', marginBottom: '0.6rem' }}>
                                <div style={{ display: 'flex', gap: '0.8rem', flexWrap: 'wrap', alignItems: 'baseline' }}>
                                    <strong>
                                        {t('Paket', 'Package')} #{p.id}
                                    </strong>
                                    <span>{p.pohrana ? `${p.poslovnica ?? ''}, POHRANA` : (p.poslovnica ?? '')}</span>
                                    <span className="muted">
                                        {t(`${p.broj_narudzbi} narudžbi, ${formatNumber(p.broj_komada, locale)} kom., ${formatMoney(p.iznos_eur, locale)}`, `${p.broj_narudzbi} orders, ${formatNumber(p.broj_komada, locale)} pcs, ${formatMoney(p.iznos_eur, locale)}`)}
                                        {p.datum_pakiranja ? `, ${t('pakirano', 'packed')} ${formatDate(p.datum_pakiranja, locale)}` : ''}
                                        {p.pakirao ? `, ${p.pakirao}` : ''}
                                    </span>
                                    <span style={{ flex: 1 }} />
                                    <button
                                        type="button"
                                        className="compact"
                                        disabled={busy || p.prebacene.length > 0 || p.rows.length === 0 || (!p.pohrana && nacin === '')}
                                        onClick={() => packageAction(p, 'send')}
                                    >
                                        {p.pohrana ? t('Označi pohranjeno', 'Mark stored') : t('Označi poslano', 'Mark sent')}
                                    </button>
                                    <button type="button" className="secondary compact" disabled={busy} onClick={() => packageAction(p, 'cancel')}>
                                        {t('Otkaži paket', 'Cancel package')}
                                    </button>
                                </div>
                                {!p.pohrana && nacin === '' ? (
                                    <p className="muted" style={{ fontSize: '0.8rem', margin: '0.2rem 0 0' }}>
                                        {t('Za slanje paketa odaberi način slanja gore.', 'Choose a carrier above to send the package.')}
                                    </p>
                                ) : null}
                                {p.prebacene.length > 0 ? (
                                    <p role="alert" style={{ color: '#a00', margin: '0.3rem 0 0', fontWeight: 600 }}>
                                        {t('U paketu je prebačena narudžba, izvadi je prije slanja.', 'The package holds a transferred order, take it out before sending.')}{' '}
                                        {p.prebacene.map((x) => (
                                            <span key={x.order_number} style={{ marginRight: '0.6rem' }}>
                                                {x.order_number} PREBAČENO u {x.nova_narudzba}{' '}
                                                <button type="button" className="secondary compact" disabled={busy} onClick={() => packageAction(p, 'remove', x.order_number)}>
                                                    {t('Izvadi iz paketa', 'Take out')}
                                                </button>
                                            </span>
                                        ))}
                                    </p>
                                ) : null}
                                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.86rem', marginTop: '0.3rem' }}>
                                    <tbody>
                                        {p.rows.map((r) => (
                                            <tr key={r.order_number}>
                                                <td style={td}>{r.order_number}</td>
                                                <td style={td}>{r.mjesto_isporuke ?? ''}</td>
                                                <td style={num}>{formatMoney(r.iznos_eur, locale)}</td>
                                                <td style={td}>{r.items.map((i) => `${i.naziv} x ${formatNumber(i.kolicina, locale)}`).join(', ')}</td>
                                                <td style={td}>{r.napomena ?? ''}</td>
                                                <td style={{ ...td, textAlign: 'right' }}>
                                                    <button type="button" className="secondary compact" disabled={busy} onClick={() => packageAction(p, 'remove', r.order_number)}>
                                                        {t('Izvadi iz paketa', 'Take out')}
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        ))}
                        {packedLoose.length === 0 ? (
                            packages.length === 0 ? <p className="muted">{t('Nema narudžbi.', 'No orders.')}</p> : null
                        ) : (
                            <div style={{ overflowX: 'auto' }}>
                                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.88rem' }}>
                                    <thead>
                                        <tr>
                                            <th style={th}>
                                                <input
                                                    type="checkbox"
                                                    aria-label={t('Označi sve', 'Select all')}
                                                    checked={packedLoose.every((r) => selected.has(r.order_number))}
                                                    onChange={() => toggleGroup(packedLoose)}
                                                />
                                            </th>
                                            <th style={th}>{t('Poslovnica', 'Branch')}</th>
                                            <th style={th}>{t('Mjesto isporuke', 'Delivery place')}</th>
                                            <th style={th}>{t('Narudžba', 'Order')}</th>
                                            <th style={th}>{t('Datum pakiranja', 'Packed on')}</th>
                                            <th style={{ ...th, textAlign: 'right' }}>{t('Iznos s PDV-om', 'Amount incl. VAT')}</th>
                                            <th style={th}>{t('Artikli', 'Items')}</th>
                                            <th style={th}>{t('Napomena', 'Note')}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {packedLoose.map((r) => (
                                            <tr key={r.order_number}>
                                                <td style={td}>
                                                    <input type="checkbox" checked={selected.has(r.order_number)} onChange={() => toggle(r.order_number)} />
                                                </td>
                                                <td style={td}>{r.poslovnica}</td>
                                                <td style={td}>{r.mjesto_isporuke ?? ''}</td>
                                                <td style={td}>{r.order_number}</td>
                                                <td style={td}>{r.datum_pakiranja ? formatDate(r.datum_pakiranja, locale) : ''}</td>
                                                <td style={num}>{formatMoney(r.iznos_eur, locale)}</td>
                                                <td style={td}>{r.items.map((i) => `${i.naziv} x ${formatNumber(i.kolicina, locale)}`).join(', ')}</td>
                                                <td style={td}>{r.napomena ?? ''}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </section>

                    <section style={{ marginTop: '1.2rem' }}>
                        <h3 style={{ margin: '0 0 0.4rem' }}>
                            {t('Prebačeno, ne šalji', 'Transferred, do not send')} ({transferred.length})
                        </h3>
                        <p className="muted" style={{ margin: '0 0 0.4rem', fontSize: '0.82rem' }}>
                            {t(
                                'Narudžba je u Farosu prebačena u drugu poslovnicu, pa se ova stara ne pakira i ne šalje. Roba ide po novoj narudžbi.',
                                'The order was moved to another branch in Faros, so this old order is not packed or sent. Goods follow the new order.',
                            )}
                        </p>
                        {transferredPacked.length > 0 ? (
                            <p role="alert" style={{ color: '#a00', margin: '0 0 0.4rem', fontWeight: 600 }}>
                                {t(
                                    `Upozorenje, ${transferredPacked.length} prebačenih narudžbi je već pakirano. Izvadite ih iz paketa prije slanja.`,
                                    `Warning, ${transferredPacked.length} transferred orders are already packed. Take them out of the package before sending.`,
                                )}
                            </p>
                        ) : null}
                        {transferred.length === 0 ? (
                            <p className="muted">{t('Nema narudžbi.', 'No orders.')}</p>
                        ) : (
                            <div style={{ overflowX: 'auto' }}>
                                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.88rem' }}>
                                    <thead>
                                        <tr>
                                            <th style={th}>{t('Poslovnica', 'Branch')}</th>
                                            <th style={th}>{t('Stara narudžba', 'Old order')}</th>
                                            <th style={th}>{t('Datum unosa', 'Entered')}</th>
                                            <th style={{ ...th, textAlign: 'right' }}>{t('Iznos s PDV-om', 'Amount incl. VAT')}</th>
                                            <th style={th}>{t('Nova narudžba', 'New order')}</th>
                                            <th style={th}>{t('Nova poslovnica', 'New branch')}</th>
                                            <th style={th}>{t('Status u Farosu', 'Faros status')}</th>
                                            <th style={th}>{t('Pakirano', 'Packed')}</th>
                                            <th style={th}>{t('Napomena', 'Note')}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {transferred.map((r) => (
                                            <tr key={r.order_number} style={r.vec_pakirano ? { background: '#eee' } : undefined}>
                                                <td style={td}>{r.poslovnica}</td>
                                                <td style={td}>
                                                    {r.order_number} <strong>PREBAČENO</strong>
                                                </td>
                                                <td style={td}>{r.datum_unosa ? formatDate(r.datum_unosa, locale) : ''}</td>
                                                <td style={num}>{formatMoney(r.iznos_eur, locale)}</td>
                                                <td style={td}>{r.nova_narudzba}</td>
                                                <td style={td}>{r.nova_poslovnica ?? ''}</td>
                                                <td style={td}>{r.faros_status ?? ''}</td>
                                                <td style={td}>
                                                    {r.vec_pakirano
                                                        ? t(
                                                              `Da${r.datum_pakiranja ? `, ${formatDate(r.datum_pakiranja, locale)}` : ''}, izvaditi iz paketa`,
                                                              `Yes${r.datum_pakiranja ? `, ${formatDate(r.datum_pakiranja, locale)}` : ''}, take out`,
                                                          )
                                                        : ''}
                                                    {r.paket_id !== null
                                                        ? (() => {
                                                              const p = packages.find((x) => x.id === r.paket_id);
                                                              return p ? (
                                                                  <>
                                                                      {' '}
                                                                      <button type="button" className="secondary compact" disabled={busy} onClick={() => packageAction(p, 'remove', r.order_number)}>
                                                                          {t(`Izvadi iz paketa #${p.id}`, `Take out of package #${p.id}`)}
                                                                      </button>
                                                                  </>
                                                              ) : null;
                                                          })()
                                                        : null}
                                                </td>
                                                <td style={td}>{r.napomena ?? ''}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </section>

                    <section style={{ marginTop: '1.2rem' }}>
                        <button type="button" className="secondary compact" onClick={() => setShowIncomplete((v) => !v)}>
                            {showIncomplete ? '▾' : '▸'} {t('Nekompletne narudžbe', 'Incomplete orders')} ({incomplete.length})
                        </button>
                        {showIncomplete ? renderTable(incomplete, { markable: false, title: t('Nekompletno, ne šalje se', 'Incomplete, not sent') }) : null}
                    </section>

                    <p className="muted" style={{ fontSize: '0.8rem', marginTop: '1.2rem' }}>
                        {t('Izuzeto iz popisa', 'Excluded')}:{' '}
                        {t(`već označene ${data.excluded.handled ?? 0}`, `already marked ${data.excluded.handled ?? 0}`)},{' '}
                        {t(`Zlatna štednja ${data.excluded.zlatna_stednja ?? 0}`, `Gold savings ${data.excluded.zlatna_stednja ?? 0}`)},{' '}
                        {t(`prebačene ${data.excluded.prebaceno ?? 0}`, `transferred ${data.excluded.prebaceno ?? 0}`)}.
                    </p>
                </>
            ) : null}

            <OrderTransfersPanel locale={locale} token={token} />
        </div>
    );
}
